import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { Close as CloseIcon, Search as SearchIcon } from "@mui/icons-material";

import { FaChevronDown, FaChevronUp, FaCopy, FaEdit, FaEye, FaFileCsv, FaPaperclip, FaSave, FaSearch, FaTimes, FaTrash } from "react-icons/fa";

import "./CadastroImoveis.css";
import ThemeProvider from "../theme/TemaMuiProvider";
import muiTheme from "../theme/muiTheme";
import { limparCpfCnpj, mascararCpfCnpj as mascararDocumento, tipoDocumento } from "../utils/documento";

const API_URL = "/api/ditr-anual";

const ESTADO_INICIAL = {
  id: null,

  // DADOS DO IMÓVEL RURAL
  ano: new Date().getFullYear(),
  ditrId: "",
  codigoAnual: "",
  codigoDitr: "",
  codigoCcir: "",
  nomeImovelRural: "",
  apelido: "",

  pertenceCondominio: "NÃO",
  imovelImuneIsento: "NÃO",
  declaracaoRetificadora: "NÃO",

  // IDENTIFICAÇÃO DO CONTRIBUINTE
  documentoContribuinte: "",
  tipoContribuinte: "",
  nomeContribuinte: "",

  // DISTRIBUIÇÃO DA ÁREA DO IMÓVEL RURAL
  areaTotalImovel: "",
  areaPreservacaoPermanente: "",
  areaReservaParticularPatrimonioNatural: "",
  areaInteresseEcologico: "",
  areaServidaoAmbiental: "",
  areaCobertaFlorestasNativas: "",
  areaAlagadaReservatorioUsina: "",
  areaTributavel: "",
  areaBenfeitoriasAtividadeRural: "",
  areaAproveitavel: "",

  // DISTRIBUIÇÃO DA ÁREA UTILIZADA
  areaProdutosVegetais: "",
  areaDescanso: "",
  areaReflorestamento: "",
  areaPastagem: "",
  areaExploracaoExtrativa: "",
  areaAtividadeGranjeiraAquicola: "",
  areaFrustracaoSafraCalamidade: "",
  areaUtilizadaAtividadeRural: "",
  grauUtilizacao: "",

  // INFORMAÇÕES AMBIENTAIS
  numeroReciboAdaIbama: "",
  numeroCar: "",

  // ÁREA NÃO UTILIZADA
  areaTotalNaoUtilizada: "",

  // CÁLCULO DO VALOR DA TERRA NUA
  valorTotalImovel: "",
  valorConstrucoesBenfeitorias: "",
  valorCulturasPastagensFlorestas: "",
  valorTerraNua: "",

  // CÁLCULO DO IMPOSTO
  valorTerraNuaTributavel: "",
  aliquota: "",
  impostoCalculado: "",
  impostoDevido: "",
  quantidadeQuotas: "",
  valorQuota: "",

  // ATIVIDADE PECUÁRIA
  quantidadeCabecasRebanho: "",

  // ATIVIDADE EXTRATIVA
  areaExploracaoExtrativaAceita: "",
  statusExtrativa: "CONFORME",
  observacaoNaoConforme: "",
  dataAvisoNaoConforme: "",

  // OBSERVAÇÃO GERAL
  observacao: "",
};

const OPCOES_SIM_NAO = ["SIM", "NÃO"];

function obterToken() {
  return localStorage.getItem("token") || sessionStorage.getItem("token") || "";
}

function criarHeaders(incluirJson = true) {
  const token = obterToken();
  const headers = {};

  if (incluirJson) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

async function lerResposta(response) {
  const tipo = response.headers.get("content-type") || "";

  if (tipo.includes("application/json")) {
    return response.json();
  }

  const texto = await response.text();

  return texto ? { erro: texto } : {};
}

async function requisicao(url, options = {}) {
  const response = await fetch(url, options);
  const dados = await lerResposta(response);

  if (!response.ok) {
    throw new Error(dados.erro || dados.mensagem || `Erro HTTP ${response.status}.`);
  }

  return dados;
}

function textoMaiusculo(valor) {
  return String(valor ?? "").toUpperCase();
}

function normalizarBusca(valor) {
  return String(valor ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .trim();
}
function somenteDigitosVirgula(valor = "") {
  let texto = String(valor)
    .replace(/\./g, "")
    .replace(/[^\d,]/g, "");

  const primeiraVirgula = texto.indexOf(",");

  if (primeiraVirgula !== -1) {
    texto = texto.slice(0, primeiraVirgula + 1) + texto.slice(primeiraVirgula + 1).replace(/,/g, "");
  }

  return texto;
}

function adicionarPontosMilhar(valor = "") {
  return String(valor).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

function mascararAreaDigitacao(valor = "") {
  const texto = somenteDigitosVirgula(valor);
  const possuiVirgula = texto.includes(",");

  const [inteiroOriginal, decimalOriginal = ""] = texto.split(",");

  const inteiro = adicionarPontosMilhar(inteiroOriginal.replace(/\D/g, ""));

  const decimal = decimalOriginal.replace(/\D/g, "").slice(0, 4);

  if (!possuiVirgula) {
    return inteiro;
  }

  return `${inteiro || "0"},${decimal}`;
}

function finalizarArea(valor = "") {
  const texto = mascararAreaDigitacao(valor);

  if (!texto) {
    return "";
  }

  if (!texto.includes(",")) {
    return `${texto},0000`;
  }

  const [inteiro, decimal = ""] = texto.split(",");

  return `${inteiro || "0"},${decimal.slice(0, 4).padEnd(4, "0")}`;
}
function mascararMoeda(valor = "") {
  const digitos = String(valor).replace(/\D/g, "");

  if (!digitos) {
    return "";
  }

  const numero = Number(digitos) / 100;

  return numero.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function moedaParaNumero(valor = "") {
  if (!valor) {
    return 0;
  }

  const texto = String(valor)
    .replace(/[R$\s]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");

  const numero = Number(texto);

  return Number.isFinite(numero) ? numero : 0;
}

function numeroParaMoeda(numero) {
  if (!Number.isFinite(Number(numero))) {
    return "";
  }

  return Number(numero).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// CPF ou CNPJ (numérico ou alfanumérico).
function mascararCpfCnpj(valor = "") {
  return mascararDocumento(valor);
}

function identificarTipoDocumento(valor = "") {
  const tipo = tipoDocumento(valor);

  if (tipo === "CPF") {
    return "PESSOA FÍSICA";
  }

  if (tipo === "CNPJ") {
    return "PESSOA JURÍDICA";
  }

  return "";
}

function formatarArea(valor) {
  if (valor === undefined || valor === null || valor === "") {
    return "-";
  }

  const numero = Number(valor);

  if (!Number.isFinite(numero)) {
    return String(valor);
  }

  return numero.toLocaleString("pt-BR", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
}

function extrairNomeArquivo(contentDisposition, nomePadrao = "arquivo") {
  if (!contentDisposition) {
    return nomePadrao;
  }

  const utf8 = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);

  if (utf8?.[1]) {
    return decodeURIComponent(utf8[1]);
  }

  const simples = contentDisposition.match(/filename="?([^";]+)"?/i);

  return simples?.[1] || nomePadrao;
}

export default function DITRAnual() {
  const [registros, setRegistros] = useState([]);
  const [ditrs, setDitrs] = useState([]);
  const [form, setForm] = useState(ESTADO_INICIAL);

  const [anoSelecionado, setAnoSelecionado] = useState("");

  const [anosDisponiveis, setAnosDisponiveis] = useState([]);

  const [paginaDitr, setPaginaDitr] = useState(0);

  const [linhasPorPaginaDitr, setLinhasPorPaginaDitr] = useState(10);

  const [busca, setBusca] = useState("");
  const [buscaDitr, setBuscaDitr] = useState("");

  const [pagina, setPagina] = useState(0);
  const [linhasPorPagina, setLinhasPorPagina] = useState(10);

  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);

  const [consultandoDocumento, setConsultandoDocumento] = useState(false);

  const [nomeBloqueadoReceita, setNomeBloqueadoReceita] = useState(false);

  // ===============================
  // VISUALIZAÇÃO / EDIÇÃO
  // ===============================
  const [visualizandoId, setVisualizandoId] = useState(null);

  const [editandoId, setEditandoId] = useState(null);

  const [modoVisualizacao, setModoVisualizacao] = useState(false);

  // ===============================
  // MODAL DE SELEÇÃO DO DITR
  // ===============================
  const [modalDitrAberto, setModalDitrAberto] = useState(false);

  // ===============================
  // GRID
  // ===============================
  const [gridMinimizado, setGridMinimizado] = useState(() => localStorage.getItem("ditrAnualGridMinimizado") === "1");

  // ===============================
  // GED
  // ===============================
  const [gedAberto, setGedAberto] = useState(false);

  const [registroGed, setRegistroGed] = useState(null);

  const [arquivosGed, setArquivosGed] = useState([]);

  const [arquivosPendentes, setArquivosPendentes] = useState([]);

  const [carregandoGed, setCarregandoGed] = useState(false);

  const [arquivoExcluindo, setArquivoExcluindo] = useState(null);

  const [secoes, setSecoes] = useState({
    dadosImovel: true,
    identificacaoContribuinte: true,
    distribuicaoAreaImovel: true,
    distribuicaoAreaUtilizada: true,
    informacoesAmbientais: true,
    areaNaoUtilizada: true,
    valorTerraNua: true,
    calculoImposto: true,
    atividadePecuaria: true,
    atividadeExtrativa: true,
  });

  const alternarSecao = (secao) => {
    setSecoes((anterior) => ({
      ...anterior,
      [secao]: !anterior[secao],
    }));
  };

  const editando = Boolean(editandoId);

  // ===============================
  // PROGRESSO DE PREENCHIMENTO
  // DAS SEÇÕES DO FORMULÁRIO
  // ===============================

  const campoPreenchido = (valor) => {
    if (valor === null || valor === undefined) {
      return false;
    }

    if (typeof valor === "string") {
      return valor.trim() !== "";
    }

    return true;
  };

  const calcularProgresso = (campos) => {
    const total = campos.length;

    const preenchidos = campos.filter(campoPreenchido).length;

    return {
      preenchidos,
      total,
      completo: total > 0 && preenchidos === total,
    };
  };

  const progressoSecoes = useMemo(() => {
    return {
      dadosImovel: calcularProgresso([form.ano, form.ditrId, form.pertenceCondominio, form.imovelImuneIsento, form.declaracaoRetificadora]),

      identificacaoContribuinte: calcularProgresso([form.documentoContribuinte, form.tipoContribuinte, form.nomeContribuinte]),

      distribuicaoAreaImovel: calcularProgresso([
        form.areaTotalImovel,
        form.areaPreservacaoPermanente,
        form.areaReservaParticularPatrimonioNatural,
        form.areaInteresseEcologico,
        form.areaServidaoAmbiental,
        form.areaCobertaFlorestasNativas,
        form.areaAlagadaReservatorioUsina,
        form.areaTributavel,
        form.areaBenfeitoriasAtividadeRural,
        form.areaAproveitavel,
      ]),

      distribuicaoAreaUtilizada: calcularProgresso([
        form.areaProdutosVegetais,
        form.areaDescanso,
        form.areaReflorestamento,
        form.areaPastagem,
        form.areaExploracaoExtrativa,
        form.areaAtividadeGranjeiraAquicola,
        form.areaFrustracaoSafraCalamidade,
        form.areaUtilizadaAtividadeRural,
        form.grauUtilizacao,
      ]),

      informacoesAmbientais: calcularProgresso([form.numeroReciboAdaIbama, form.numeroCar]),

      areaNaoUtilizada: calcularProgresso([form.areaTotalNaoUtilizada]),

      valorTerraNua: calcularProgresso([form.valorTotalImovel, form.valorConstrucoesBenfeitorias, form.valorCulturasPastagensFlorestas, form.valorTerraNua]),

      calculoImposto: calcularProgresso([
        form.valorTerraNuaTributavel,
        form.aliquota,
        form.impostoCalculado,
        form.impostoDevido,
        form.quantidadeQuotas,
        form.valorQuota,
      ]),

      atividadePecuaria: calcularProgresso([form.quantidadeCabecasRebanho]),

      atividadeExtrativa:
        form.statusExtrativa === "NÃO CONFORME"
          ? calcularProgresso([form.areaExploracaoExtrativaAceita, form.statusExtrativa, form.observacaoNaoConforme, form.dataAvisoNaoConforme])
          : calcularProgresso([form.areaExploracaoExtrativaAceita, form.statusExtrativa]),
    };
  }, [form]);

  const indicadorSecao = (progresso) => (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.7,
        ml: 1.5,
        fontSize: "0.78rem",
        fontWeight: 500,
        color: "var(--ig-t-94a3b8)",
        textTransform: "none",
        whiteSpace: "nowrap",
      }}
    >
      <span>
        {progresso.preenchidos}/{progresso.total}
      </span>

      {progresso.completo && (
        <Box
          component="span"
          sx={{
            color: "var(--ig-t-22c55e)",
            fontWeight: 800,
            fontSize: "0.9rem",
            lineHeight: 1,
          }}
          title="Seção preenchida"
        >
          ✓
        </Box>
      )}
    </Box>
  );

  const [mensagem, setMensagem] = useState({
    aberta: false,
    texto: "",
    tipo: "success",
  });

  useEffect(() => {
    localStorage.setItem("ditrAnualGridMinimizado", gridMinimizado ? "1" : "0");
  }, [gridMinimizado]);

  useEffect(() => {
    const style = document.createElement("style");

    style.setAttribute("data-ditr-receita-style", "true");

    style.innerHTML = receitaPulseStyle;

    document.head.appendChild(style);

    return () => {
      style.remove();
    };
  }, []);

  useEffect(() => {
    const valorTributavel = moedaParaNumero(form.valorTerraNuaTributavel);

    const aliquota = Number(
      String(form.aliquota || "")
        .replace("%", "")
        .replace(",", "."),
    );

    const imposto = Number.isFinite(valorTributavel) && Number.isFinite(aliquota) ? valorTributavel * (aliquota / 100) : 0;

    const impostoFormatado = imposto > 0 ? numeroParaMoeda(imposto) : "";

    setForm((anterior) => {
      if (anterior.impostoCalculado === impostoFormatado) {
        return anterior;
      }

      return {
        ...anterior,
        impostoCalculado: impostoFormatado,
      };
    });
  }, [form.valorTerraNuaTributavel, form.aliquota]);

  useEffect(() => {
    const impostoDevido = moedaParaNumero(form.impostoDevido);

    const quantidade = Number(form.quantidadeQuotas);

    const valorQuota = Number.isFinite(impostoDevido) && Number.isInteger(quantidade) && quantidade > 0 ? impostoDevido / quantidade : 0;

    const quotaFormatada = valorQuota > 0 ? numeroParaMoeda(valorQuota) : "";

    setForm((anterior) => {
      if (anterior.valorQuota === quotaFormatada) {
        return anterior;
      }

      return {
        ...anterior,
        valorQuota: quotaFormatada,
      };
    });
  }, [form.impostoDevido, form.quantidadeQuotas]);

  const exibirMensagem = useCallback((texto, tipo = "success") => {
    setMensagem({
      aberta: true,
      texto,
      tipo,
    });
  }, []);

  const fecharMensagem = () => {
    setMensagem((anterior) => ({
      ...anterior,
      aberta: false,
    }));
  };

  const carregarRegistros = useCallback(
    async (ano = anoSelecionado) => {
      if (!ano) {
        setRegistros([]);
        setCarregando(false);
        return;
      }

      setCarregando(true);

      try {
        const dados = await requisicao(`${API_URL}/${encodeURIComponent(ano)}`, {
          headers: criarHeaders(false),
        });

        const lista = Array.isArray(dados) ? dados : [];

        setRegistros(lista);

        if (lista.length > 0) {
          setAnosDisponiveis((anteriores) => [...new Set([...anteriores, Number(ano)])].sort((a, b) => a - b));
        }
      } catch (error) {
        console.error("Erro ao carregar DITR Anual:", error);

        setRegistros([]);

        exibirMensagem(error.message || "Não foi possível carregar os registros do DITR Anual.", "error");
      } finally {
        setCarregando(false);
      }
    },
    [anoSelecionado, exibirMensagem],
  );

  const carregarAnosDisponiveis = useCallback(async () => {
    try {
      const dados = await requisicao(`${API_URL}/listar-anos`, {
        headers: criarHeaders(false),
      });

      const anos = Array.isArray(dados) ? dados.map(Number).filter(Number.isInteger) : [];

      setAnosDisponiveis([...new Set(anos)].sort((a, b) => a - b));
    } catch (error) {
      console.error("Erro ao carregar anos do DITR Anual:", error);

      setAnosDisponiveis([]);
    }
  }, []);

  const carregarDitrs = useCallback(
    async (ano = form.ano || anoSelecionado) => {
      if (!ano) {
        setDitrs([]);
        return;
      }

      try {
        const dados = await requisicao(`${API_URL}/ditrs-disponiveis?ano=${encodeURIComponent(ano)}`, {
          headers: criarHeaders(false),
        });

        setDitrs(Array.isArray(dados) ? dados : []);
      } catch (error) {
        console.error("Erro ao carregar DITRs disponíveis:", error);

        setDitrs([]);
      }
    },
    [form.ano, anoSelecionado],
  );

  useEffect(() => {
    carregarAnosDisponiveis();
  }, [carregarAnosDisponiveis]);

  useEffect(() => {
    if (!anoSelecionado) {
      setRegistros([]);
      setPagina(0);
      return;
    }

    carregarRegistros(anoSelecionado);
    carregarDitrs(anoSelecionado);
  }, [anoSelecionado, carregarRegistros, carregarDitrs]);

  useEffect(() => {
    setPaginaDitr(0);
  }, [buscaDitr]);

  const registrosFiltrados = useMemo(() => {
    const termo = normalizarBusca(busca);

    if (!termo) {
      return registros;
    }

    return registros.filter((registro) => {
      const conteudo = [
        registro.codigoAnual,
        registro.codigoDitr,
        registro.codigoCcir,
        registro.nomeImovelRural,
        registro.apelido,
        registro.ano,
        registro.observacao,
      ]
        .filter(Boolean)
        .join(" ");

      return normalizarBusca(conteudo).includes(termo);
    });
  }, [busca, registros]);

  const registrosPaginados = useMemo(() => {
    const inicio = pagina * linhasPorPagina;

    return registrosFiltrados.slice(inicio, inicio + linhasPorPagina);
  }, [pagina, linhasPorPagina, registrosFiltrados]);

  const ditrsFiltrados = useMemo(() => {
    const termo = normalizarBusca(buscaDitr);

    return (ditrs || []).filter((ditr) => {
      const registroAtual = editandoId || visualizandoId;

      if (ditr.possuiRegistroNoAno && Number(ditr.ditrAnualId) !== Number(registroAtual)) {
        return false;
      }

      if (!termo) {
        return true;
      }

      const conteudo = [
        ditr.codigoDitr,
        ditr.codigoCcir,
        ditr.codigoCar,
        ditr.registroCar,
        ditr.registroNoCar,
        ditr.nomeImovelRural,
        ditr.apelido,
        ditr.codigoMatricula,
        ditr.municipioSedeImovelRural,
        ditr.municipio,
        ditr.uf,
      ]
        .filter(Boolean)
        .join(" ");

      return normalizarBusca(conteudo).includes(termo);
    });
  }, [buscaDitr, ditrs, editandoId, visualizandoId]);

  const ditrsPaginados = useMemo(() => {
    const inicio = paginaDitr * linhasPorPaginaDitr;

    return ditrsFiltrados.slice(inicio, inicio + linhasPorPaginaDitr);
  }, [ditrsFiltrados, paginaDitr, linhasPorPaginaDitr]);

  const limparFormulario = (ano = anoSelecionado || "") => {
    if (salvando) {
      return;
    }

    setForm({
      ...ESTADO_INICIAL,
      ano: ano || new Date().getFullYear(),
    });

    setVisualizandoId(null);
    setEditandoId(null);
    setModoVisualizacao(false);

    setArquivosPendentes([]);

    setGedAberto(false);
    setRegistroGed(null);
    setArquivosGed([]);
    setCarregandoGed(false);
    setArquivoExcluindo(null);

    setConsultandoDocumento(false);
    setNomeBloqueadoReceita(false);

    setBuscaDitr("");
    setPaginaDitr(0);
  };

  const alterarCampo = (campo) => (evento) => {
    const valor = evento.target.value;

    setForm((anterior) => ({
      ...anterior,
      [campo]: textoMaiusculo(valor),
    }));
  };

  const alterarCampoArea = (campo) => (evento) => {
    const valor = mascararAreaDigitacao(evento.target.value);

    setForm((anterior) => ({
      ...anterior,
      [campo]: valor,
    }));
  };

  const finalizarCampoArea = (campo) => () => {
    setForm((anterior) => ({
      ...anterior,
      [campo]: finalizarArea(anterior[campo]),
    }));
  };

  const alterarCampoMoeda = (campo) => (evento) => {
    const valor = mascararMoeda(evento.target.value);

    setForm((anterior) => ({
      ...anterior,
      [campo]: valor,
    }));
  };
  const consultarDocumentoReceita = async (documento) => {
    const docLimpo = limparCpfCnpj(documento);

    if (docLimpo.length !== 14) {
      return null;
    }

    try {
      const token = obterToken();

      const response = await fetch(`/api/proprietarios/consulta-documento?tipo=PJ&documento=${docLimpo}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await lerResposta(response);

      if (!response.ok) {
        throw new Error(data?.erro || data?.mensagem || "CNPJ não localizado.");
      }

      return {
        nome: String(data?.nome || "")
          .trim()
          .toUpperCase(),
      };
    } catch (error) {
      console.error("Erro ao consultar CNPJ na Receita:", error);

      throw error;
    }
  };

  const alterarDocumento = async (evento) => {
    const documento = mascararCpfCnpj(evento.target.value);

    const documentoLimpo = limparCpfCnpj(documento);

    const tipoContribuinte = identificarTipoDocumento(documento);

    setForm((anterior) => ({
      ...anterior,

      documentoContribuinte: documento,

      tipoContribuinte,

      nomeContribuinte: documentoLimpo.length < 14 && anterior.tipoContribuinte === "PESSOA JURÍDICA" ? "" : anterior.nomeContribuinte,
    }));

    // Enquanto ainda não houver 14 dígitos,
    // não consulta a Receita.
    if (documentoLimpo.length !== 14) {
      setNomeBloqueadoReceita(false);
      return;
    }

    setConsultandoDocumento(true);

    try {
      const resultado = await consultarDocumentoReceita(documentoLimpo);

      if (!resultado?.nome) {
        setNomeBloqueadoReceita(false);

        exibirMensagem("CNPJ localizado, porém a Receita não retornou o nome empresarial.", "warning");

        return;
      }

      setForm((anterior) => ({
        ...anterior,

        documentoContribuinte: mascararCpfCnpj(documentoLimpo),

        tipoContribuinte: "PESSOA JURÍDICA",

        nomeContribuinte: resultado.nome,
      }));

      setNomeBloqueadoReceita(true);
    } catch (error) {
      setNomeBloqueadoReceita(false);

      exibirMensagem(error?.message || "CNPJ não localizado. Informe o nome manualmente.", "warning");
    } finally {
      setConsultandoDocumento(false);
    }
  };

  const alterarAno = (evento) => {
    const valor = evento.target.value.replace(/\D/g, "").slice(0, 4);

    setForm((anterior) => ({
      ...anterior,
      ano: valor,
      ditrId: "",
      codigoDitr: "",
      codigoCcir: "",
      nomeImovelRural: "",
      apelido: "",
      areaTotalImovel: "",
      numeroCar: "",
    }));
  };

  const selecionarDitr = (ditr) => {
    setForm((anterior) => ({
      ...anterior,

      ditrId: ditr.id ?? ditr.ditrId ?? "",

      codigoDitr: ditr.codigoDitr ?? "",

      codigoCcir: ditr.codigoCcir ?? "",

      nomeImovelRural: ditr.nomeImovelRural ?? "",

      apelido: ditr.apelido ?? "",

      areaTotalImovel:
        ditr.areaTotalHa != null
          ? finalizarArea(String(ditr.areaTotalHa).replace(".", ","))
          : ditr.areaTotalImovel != null
            ? finalizarArea(String(ditr.areaTotalImovel).replace(".", ","))
            : "",

      numeroCar: ditr.numeroCar ?? ditr.registroCar ?? ditr.registroNoCar ?? "",
    }));

    setModalDitrAberto(false);
    setBuscaDitr("");
    setPaginaDitr(0);
  };

  const formatarAreaBanco = (valor) => {
    if (valor === null || valor === undefined || valor === "") {
      return "";
    }

    return finalizarArea(String(valor).replace(".", ","));
  };

  const preencherFormulario = (registro) => ({
    ...ESTADO_INICIAL,
    ...registro,

    id: registro.id ?? null,

    ano: String(registro.ano ?? ""),

    ditrId: registro.ditrId ?? "",
    codigoAnual: registro.codigoAnual ?? "",
    codigoDitr: registro.codigoDitr ?? "",
    codigoCcir: registro.codigoCcir ?? "",
    nomeImovelRural: registro.nomeImovelRural ?? "",
    apelido: registro.apelido ?? "",

    pertenceCondominio: registro.pertenceCondominio ?? "NÃO",

    imovelImuneIsento: registro.imovelImuneIsento ?? "NÃO",

    declaracaoRetificadora: registro.declaracaoRetificadora ?? "NÃO",

    documentoContribuinte: mascararCpfCnpj(registro.documentoContribuinte ?? ""),

    tipoContribuinte: registro.tipoContribuinte ?? "",

    nomeContribuinte: registro.nomeContribuinte ?? "",

    areaTotalImovel: formatarAreaBanco(registro.areaTotalImovel),

    areaPreservacaoPermanente: formatarAreaBanco(registro.areaPreservacaoPermanente),

    areaReservaParticularPatrimonioNatural: formatarAreaBanco(registro.areaReservaParticularPatrimonioNatural),

    areaInteresseEcologico: formatarAreaBanco(registro.areaInteresseEcologico),

    areaServidaoAmbiental: formatarAreaBanco(registro.areaServidaoAmbiental),

    areaCobertaFlorestasNativas: formatarAreaBanco(registro.areaCobertaFlorestasNativas),

    areaAlagadaReservatorioUsina: formatarAreaBanco(registro.areaAlagadaReservatorioUsina),

    areaTributavel: formatarAreaBanco(registro.areaTributavel),

    areaBenfeitoriasAtividadeRural: formatarAreaBanco(registro.areaBenfeitoriasAtividadeRural),

    areaAproveitavel: formatarAreaBanco(registro.areaAproveitavel),

    areaProdutosVegetais: formatarAreaBanco(registro.areaProdutosVegetais),

    areaDescanso: formatarAreaBanco(registro.areaDescanso),

    areaReflorestamento: formatarAreaBanco(registro.areaReflorestamento),

    areaPastagem: formatarAreaBanco(registro.areaPastagem),

    areaExploracaoExtrativa: formatarAreaBanco(registro.areaExploracaoExtrativa),

    areaAtividadeGranjeiraAquicola: formatarAreaBanco(registro.areaAtividadeGranjeiraAquicola),

    areaFrustracaoSafraCalamidade: formatarAreaBanco(registro.areaFrustracaoSafraCalamidade),

    areaUtilizadaAtividadeRural: formatarAreaBanco(registro.areaUtilizadaAtividadeRural),

    grauUtilizacao: formatarAreaBanco(registro.grauUtilizacao),

    numeroReciboAdaIbama: registro.numeroReciboAdaIbama ?? "",

    numeroCar: registro.numeroCar ?? registro.registroCar ?? "",

    areaTotalNaoUtilizada: formatarAreaBanco(registro.areaTotalNaoUtilizada),

    valorTotalImovel: registro.valorTotalImovel != null ? numeroParaMoeda(registro.valorTotalImovel) : "",

    valorConstrucoesBenfeitorias: registro.valorConstrucoesBenfeitorias != null ? numeroParaMoeda(registro.valorConstrucoesBenfeitorias) : "",

    valorCulturasPastagensFlorestas: registro.valorCulturasPastagensFlorestas != null ? numeroParaMoeda(registro.valorCulturasPastagensFlorestas) : "",

    valorTerraNua: registro.valorTerraNua != null ? numeroParaMoeda(registro.valorTerraNua) : "",

    valorTerraNuaTributavel: registro.valorTerraNuaTributavel != null ? numeroParaMoeda(registro.valorTerraNuaTributavel) : "",

    aliquota: registro.aliquota != null ? String(registro.aliquota).replace(".", ",") : "",

    impostoCalculado: registro.impostoCalculado != null ? numeroParaMoeda(registro.impostoCalculado) : "",

    impostoDevido: registro.impostoDevido != null ? numeroParaMoeda(registro.impostoDevido) : "",

    quantidadeQuotas: registro.quantidadeQuotas != null ? String(registro.quantidadeQuotas) : "",

    valorQuota: registro.valorQuota != null ? numeroParaMoeda(registro.valorQuota) : "",

    quantidadeCabecasRebanho: registro.quantidadeCabecasRebanho != null ? String(registro.quantidadeCabecasRebanho) : "",

    areaExploracaoExtrativaAceita: formatarAreaBanco(registro.areaExploracaoExtrativaAceita),

    statusExtrativa: registro.statusExtrativa ?? "CONFORME",

    observacaoNaoConforme: registro.observacaoNaoConforme ?? "",

    dataAvisoNaoConforme: registro.dataAvisoNaoConforme ? String(registro.dataAvisoNaoConforme).slice(0, 10) : "",

    observacao: registro.observacao ?? "",
  });

  const visualizarRegistro = async (registro) => {
    const id = registro?.id ?? registro?.ditrAnualId;

    if (!id) {
      exibirMensagem("Registro do DITR Anual inválido.", "error");
      return;
    }

    try {
      const completo = await requisicao(`${API_URL}/registro/${id}`, {
        headers: criarHeaders(false),
      });

      setForm(preencherFormulario(completo));

      setAnoSelecionado(Number(completo.ano));

      setVisualizandoId(id);
      setEditandoId(null);
      setModoVisualizacao(true);

      setConsultandoDocumento(false);
      setNomeBloqueadoReceita(false);

      setArquivosPendentes([]);

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } catch (error) {
      console.error("Erro ao visualizar DITR Anual:", error);

      exibirMensagem(error.message || "Não foi possível consultar o DITR Anual.", "error");
    }
  };

  const iniciarEdicao = (evento) => {
    if (evento) {
      evento.preventDefault();
      evento.stopPropagation();
    }

    if (!visualizandoId) {
      return;
    }

    setEditandoId(visualizandoId);
    setModoVisualizacao(false);
  };

  const validarFormulario = () => {
    const ano = Number(form.ano);

    if (!Number.isInteger(ano) || ano < 1900 || ano > 2100) {
      return "Informe um ano válido.";
    }

    if (!form.ditrId) {
      return "Selecione um DITR.";
    }

    return null;
  };

  const enviarGed = async (registroId, arquivos) => {
    if (!registroId || !arquivos.length) {
      return;
    }

    const formData = new FormData();

    arquivos.forEach((arquivo) => {
      formData.append("files", arquivo);
    });

    const token = obterToken();
    const headers = {};

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    await requisicao(`${API_URL}/${registroId}/ged`, {
      method: "POST",
      headers,
      body: formData,
    });
  };

  const carregarArquivosGed = useCallback(
    async (registroId) => {
      if (!registroId) {
        return;
      }

      setCarregandoGed(true);

      try {
        const dados = await requisicao(`${API_URL}/${registroId}/ged`, {
          headers: criarHeaders(false),
        });

        setArquivosGed(Array.isArray(dados) ? dados : []);
      } catch (error) {
        console.error("Erro ao carregar GED do DITR Anual:", error);

        setArquivosGed([]);

        exibirMensagem(error.message || "Não foi possível carregar os documentos.", "error");
      } finally {
        setCarregandoGed(false);
      }
    },
    [exibirMensagem],
  );

  const abrirGed = async (registro) => {
    const registroId = registro?.id ?? registro?.ditrAnualId;

    if (!registroId) {
      exibirMensagem("Não foi possível identificar o registro do DITR Anual.", "error");
      return;
    }

    setRegistroGed({
      ...registro,
      id: registroId,
    });

    setArquivosGed([]);
    setGedAberto(true);

    await carregarArquivosGed(registroId);
  };

  const fecharGed = () => {
    if (arquivoExcluindo) {
      return;
    }

    setGedAberto(false);
    setRegistroGed(null);
    setArquivosGed([]);
  };

  const abrirArquivo = async (arquivo) => {
    if (!registroGed?.id || !arquivo?.id) {
      return;
    }

    const extensao = String(arquivo.nome || "")
      .split(".")
      .pop()
      .toLowerCase();

    const visualizaveis = ["pdf", "png", "jpg", "jpeg", "gif", "bmp", "webp", "svg", "txt"];

    if (!visualizaveis.includes(extensao)) {
      const confirmar = window.confirm(
        `Visualização indisponível.\n\n` + `O arquivo "${arquivo.nome}" não possui visualização integrada no ImobGest.\n\n` + `Deseja baixar agora?`,
      );

      if (confirmar) {
        await baixarArquivo(arquivo);
      }

      return;
    }

    try {
      const token = obterToken();

      const response = await fetch(`${API_URL}/${registroGed.id}/ged/${arquivo.id}/visualizar`, {
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : {},
      });

      if (!response.ok) {
        const dados = await lerResposta(response);

        throw new Error(dados.erro || "Erro ao visualizar o documento.");
      }

      const blob = await response.blob();

      const url = URL.createObjectURL(blob);

      window.open(url, "_blank", "noopener,noreferrer");

      window.setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 60000);
    } catch (error) {
      console.error("Erro ao visualizar documento:", error);

      exibirMensagem(error.message || "Não foi possível visualizar o documento.", "error");
    }
  };

  const baixarArquivo = async (arquivo) => {
    if (!registroGed?.id || !arquivo?.id) {
      return;
    }

    try {
      const token = obterToken();

      const response = await fetch(`${API_URL}/${registroGed.id}/ged/${arquivo.id}`, {
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : {},
      });

      if (!response.ok) {
        const dados = await lerResposta(response);

        throw new Error(dados.erro || "Erro ao baixar o documento.");
      }

      const blob = await response.blob();

      const url = URL.createObjectURL(blob);

      const nome = extrairNomeArquivo(response.headers.get("content-disposition"), arquivo.nome || "documento");

      const link = document.createElement("a");

      link.href = url;
      link.download = nome;

      document.body.appendChild(link);

      link.click();
      link.remove();

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao baixar documento:", error);

      exibirMensagem(error.message || "Não foi possível baixar o documento.", "error");
    }
  };

  const excluirArquivo = async (arquivo) => {
    if (!registroGed?.id || !arquivo?.id) {
      return;
    }

    const confirmou = window.confirm(`Deseja realmente excluir o arquivo "${arquivo.nome}"?`);

    if (!confirmou) {
      return;
    }

    setArquivoExcluindo(arquivo.id);

    try {
      const resposta = await requisicao(`${API_URL}/${registroGed.id}/ged/${arquivo.id}`, {
        method: "DELETE",
        headers: criarHeaders(false),
      });

      exibirMensagem(resposta.mensagem || "Arquivo excluído com sucesso!", "success");

      await carregarArquivosGed(registroGed.id);

      if (anoSelecionado) {
        await carregarRegistros(anoSelecionado);
      }
    } catch (error) {
      console.error("Erro ao excluir documento:", error);

      exibirMensagem(error.message || "Não foi possível excluir o documento.", "error");
    } finally {
      setArquivoExcluindo(null);
    }
  };

  const salvar = async () => {
    const erro = validarFormulario();

    if (erro) {
      exibirMensagem(erro, "warning");
      return;
    }

    setSalvando(true);

    try {
      const payload = {
        ano: Number(form.ano),
        ditrId: Number(form.ditrId),

        pertenceCondominio: form.pertenceCondominio,

        imovelImuneIsento: form.imovelImuneIsento,

        declaracaoRetificadora: form.declaracaoRetificadora,

        documentoContribuinte: limparCpfCnpj(form.documentoContribuinte),

        tipoContribuinte: form.tipoContribuinte,

        nomeContribuinte: textoMaiusculo(form.nomeContribuinte).trim(),

        areaTotalImovel: form.areaTotalImovel,

        areaPreservacaoPermanente: form.areaPreservacaoPermanente,

        areaReservaParticularPatrimonioNatural: form.areaReservaParticularPatrimonioNatural,

        areaInteresseEcologico: form.areaInteresseEcologico,

        areaServidaoAmbiental: form.areaServidaoAmbiental,

        areaCobertaFlorestasNativas: form.areaCobertaFlorestasNativas,

        areaAlagadaReservatorioUsina: form.areaAlagadaReservatorioUsina,

        areaTributavel: form.areaTributavel,

        areaBenfeitoriasAtividadeRural: form.areaBenfeitoriasAtividadeRural,

        areaAproveitavel: form.areaAproveitavel,

        areaProdutosVegetais: form.areaProdutosVegetais,

        areaDescanso: form.areaDescanso,

        areaReflorestamento: form.areaReflorestamento,

        areaPastagem: form.areaPastagem,

        areaExploracaoExtrativa: form.areaExploracaoExtrativa,

        areaAtividadeGranjeiraAquicola: form.areaAtividadeGranjeiraAquicola,

        areaFrustracaoSafraCalamidade: form.areaFrustracaoSafraCalamidade,

        areaUtilizadaAtividadeRural: form.areaUtilizadaAtividadeRural,

        grauUtilizacao: form.grauUtilizacao,

        numeroReciboAdaIbama: textoMaiusculo(form.numeroReciboAdaIbama).trim(),

        numeroCar: textoMaiusculo(form.numeroCar).trim(),

        areaTotalNaoUtilizada: form.areaTotalNaoUtilizada,

        valorTotalImovel: moedaParaNumero(form.valorTotalImovel),

        valorConstrucoesBenfeitorias: moedaParaNumero(form.valorConstrucoesBenfeitorias),

        valorCulturasPastagensFlorestas: moedaParaNumero(form.valorCulturasPastagensFlorestas),

        valorTerraNua: moedaParaNumero(form.valorTerraNua),

        valorTerraNuaTributavel: moedaParaNumero(form.valorTerraNuaTributavel),

        aliquota: Number(String(form.aliquota).replace("%", "").replace(",", ".")),

        impostoCalculado: moedaParaNumero(form.impostoCalculado),

        impostoDevido: moedaParaNumero(form.impostoDevido),

        quantidadeQuotas: Number(form.quantidadeQuotas || 0),

        valorQuota: moedaParaNumero(form.valorQuota),

        quantidadeCabecasRebanho: Number(String(form.quantidadeCabecasRebanho).replace(/\D/g, "")),

        areaExploracaoExtrativaAceita: form.areaExploracaoExtrativaAceita,

        statusExtrativa: form.statusExtrativa,

        observacaoNaoConforme: form.statusExtrativa === "NÃO CONFORME" ? textoMaiusculo(form.observacaoNaoConforme).trim() : "",

        dataAvisoNaoConforme: form.statusExtrativa === "NÃO CONFORME" ? form.dataAvisoNaoConforme || null : null,

        observacao: textoMaiusculo(form.observacao).trim(),
      };

      const ano = Number(form.ano);

      const url = editandoId ? `${API_URL}/${ano}/${editandoId}` : `${API_URL}/${ano}`;

      const resposta = await requisicao(url, {
        method: editandoId ? "PUT" : "POST",
        headers: criarHeaders(true),
        body: JSON.stringify(payload),
      });

      const registroId = editandoId || resposta.ditrAnualId || resposta.id;

      if (registroId && arquivosPendentes.length > 0) {
        await enviarGed(registroId, arquivosPendentes);
      }

      exibirMensagem(resposta.mensagem || "DITR Anual salvo com sucesso!", "success");

      const anoSalvo = Number(form.ano);

      setAnoSelecionado(anoSalvo);

      await Promise.all([carregarRegistros(anoSalvo), carregarDitrs(anoSalvo)]);

      await carregarAnosDisponiveis();

      if (editandoId) {
        const id = editandoId;

        setVisualizandoId(id);
        setEditandoId(null);
        setModoVisualizacao(true);

        const completo = await requisicao(`${API_URL}/registro/${id}`, {
          headers: criarHeaders(false),
        });

        setForm(preencherFormulario(completo));
      } else {
        limparFormulario(anoSalvo);
      }
    } catch (error) {
      console.error("Erro ao salvar DITR Anual:", error);

      exibirMensagem(error.message || "Não foi possível salvar o DITR Anual.", "error");
    } finally {
      setSalvando(false);
    }
  };

  const excluirRegistro = async () => {
    const id = visualizandoId || editandoId || form.id;

    if (!id) {
      return;
    }

    const confirmou = window.confirm(`Deseja excluir o registro ${form.codigoAnual || form.id}?`);

    if (!confirmou) {
      return;
    }

    setExcluindo(true);

    try {
      const resposta = await requisicao(`${API_URL}/${form.ano}/${id}`, {
        method: "DELETE",
        headers: criarHeaders(false),
      });

      exibirMensagem(resposta.mensagem || "DITR Anual excluído com sucesso!", "success");

      const ano = Number(form.ano);

      limparFormulario(ano);

      await Promise.all([carregarRegistros(ano), carregarDitrs(ano)]);

      await carregarAnosDisponiveis();
    } catch (error) {
      exibirMensagem(error.message || "Não foi possível excluir o DITR Anual.", "error");
    } finally {
      setExcluindo(false);
    }
  };

  const selecionarArquivos = (evento) => {
    const novosArquivos = Array.from(evento.target.files || []);

    if (!novosArquivos.length) {
      return;
    }

    setArquivosPendentes((anteriores) => [...anteriores, ...novosArquivos]);

    evento.target.value = "";
  };

  const removerArquivo = (indice) => {
    setArquivosPendentes((anteriores) => anteriores.filter((_, indiceAtual) => indiceAtual !== indice));
  };

  const copiarTabela = async () => {
    const linhas = registrosFiltrados.map((registro) =>
      [
        registro.codigoAnual || "",
        registro.ano || "",
        registro.codigoDitr || "",
        registro.codigoCcir || "",
        registro.nomeImovelRural || "",
        registro.apelido || "",
        formatarArea(registro.areaTotalHa),
      ].join("\t"),
    );

    const conteudo = ["CÓDIGO\tANO\tDITR\tCCIR\tIMÓVEL\tAPELIDO\tÁREA", ...linhas].join("\n");

    await navigator.clipboard.writeText(conteudo);

    exibirMensagem("Registros copiados com sucesso!");
  };

  const exportarCSV = () => {
    const escapar = (valor) => `"${String(valor ?? "").replace(/"/g, '""')}"`;

    const linhas = registrosFiltrados.map((registro) =>
      [
        registro.codigoAnual,
        registro.ano,
        registro.codigoDitr,
        registro.codigoCcir,
        registro.nomeImovelRural,
        registro.apelido,
        formatarArea(registro.areaTotalHa),
        registro.observacao,
      ]
        .map(escapar)
        .join(";"),
    );

    const conteudo = [["CÓDIGO", "ANO", "DITR", "CCIR", "IMÓVEL", "APELIDO", "ÁREA TOTAL", "OBSERVAÇÃO"].map(escapar).join(";"), ...linhas].join("\n");

    const blob = new Blob([`\uFEFF${conteudo}`], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "ditr-anual.csv";

    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(url);
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <div className="local-container">
        <h2>DITR ANUAL</h2>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 3,
          }}
        >
          <FormControl
            size="small"
            sx={{
              minWidth: 320,
              maxWidth: 360,

              "& .MuiOutlinedInput-root": {
                background: "var(--ig-f-1b2433)",
                color: "var(--ig-t-ffffff)",
                borderRadius: "10px",

                "& fieldset": {
                  borderColor: "var(--ig-b-314158)",
                },

                "&:hover fieldset": {
                  borderColor: "#2ea8ff",
                },

                "&.Mui-focused fieldset": {
                  borderColor: "#2ea8ff",
                },
              },

              "& .MuiInputLabel-root": {
                color: "var(--ig-t-c9d4e5)",
              },

              "& .MuiInputLabel-root.Mui-focused": {
                color: "#2ea8ff",
              },

              "& .MuiSvgIcon-root": {
                color: "var(--ig-t-ffffff)",
              },
            }}
          >
            <InputLabel>Buscar Ano</InputLabel>

            <Select
              value={anoSelecionado}
              label="Buscar Ano"
              onChange={(evento) => {
                const valor = evento.target.value;

                setBusca("");
                setPagina(0);
                setPaginaDitr(0);

                if (!valor) {
                  setAnoSelecionado("");
                  setRegistros([]);
                  limparFormulario("");
                  return;
                }

                const ano = Number(valor);

                setAnoSelecionado(ano);
                limparFormulario(ano);
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: "var(--ig-f-1b2433)",
                    color: "var(--ig-t-ffffff)",

                    "& .MuiMenuItem-root": {
                      minHeight: 42,
                    },

                    "& .MuiMenuItem-root:hover": {
                      bgcolor: "#27364d",
                    },

                    "& .Mui-selected": {
                      bgcolor: "#0d6efd !important",
                    },
                  },
                },
              }}
            >
              <MenuItem value="">Selecione...</MenuItem>

              {anosDisponiveis.map((ano) => (
                <MenuItem key={ano} value={ano}>
                  DITR {ano}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {anoSelecionado && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) auto",
              gap: 2,
              mb: 2,
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Filtrar registros do DITR Anual"
              value={busca}
              onChange={(evento) => {
                setBusca(evento.target.value);
                setPagina(0);
              }}
              sx={muiCampoSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Box
              sx={{
                display: "flex",
                gap: 1,
              }}
            >
              <Button variant="outlined" onClick={() => setBusca("")}>
                Limpar Filtro
              </Button>

              <Button variant="contained" onClick={copiarTabela}>
                <FaCopy style={{ marginRight: 6 }} />
                Copiar
              </Button>

              <Button variant="contained" onClick={exportarCSV}>
                <FaFileCsv style={{ marginRight: 6 }} />
                CSV
              </Button>
            </Box>
          </Box>
        )}
        {!anoSelecionado && (
          <div className="aviso-permissao-container">
            <p className="aviso-permissao">📅 Selecione um ano para visualizar os registros de DITR.</p>
          </div>
        )}

        <div className="grid-toggle-area right">
          {!gridMinimizado ? (
            <button type="button" className="btn-grid-toggle small danger" onClick={() => setGridMinimizado(true)} title="Minimizar o Grid">
              ↘ Minimizar
            </button>
          ) : (
            <button type="button" className="btn-grid-toggle small outline" onClick={() => setGridMinimizado(false)} title="Expandir o Grid">
              ↗ Expandir
            </button>
          )}
        </div>

        {anoSelecionado && !gridMinimizado && (
          <div className="bloco-lista">
            {carregando ? (
              <p>Carregando...</p>
            ) : registrosPaginados.length === 0 ? (
              <p>Não há registros para exibir</p>
            ) : (
              <>
                <table
                  className="tabela-matriculas"
                  style={{
                    width: "100%",
                    tableLayout: "fixed",
                  }}
                >
                  <colgroup>
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "22%" }} />
                  </colgroup>

                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Ano</th>
                      <th>DITR</th>
                      <th>CCIR</th>
                      <th>Imóvel</th>
                      <th>Área Total</th>
                      <th>Ações</th>
                    </tr>
                  </thead>

                  <tbody>
                    {registrosPaginados.map((registro) => (
                      <tr key={registro.id}>
                        <td
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={registro.codigoAnual || ""}
                        >
                          {registro.codigoAnual}
                        </td>

                        <td
                          style={{
                            whiteSpace: "nowrap",
                          }}
                        >
                          {registro.ano}
                        </td>

                        <td
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={registro.codigoDitr || ""}
                        >
                          {registro.codigoDitr}
                        </td>

                        <td
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={registro.codigoCcir || ""}
                        >
                          {registro.codigoCcir}
                        </td>

                        <td
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={registro.apelido || registro.nomeImovelRural || ""}
                        >
                          {registro.apelido || registro.nomeImovelRural}
                        </td>

                        <td
                          style={{
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatarArea(registro.areaTotalImovel ?? registro.areaTotalHa)} ha
                        </td>

                        <td>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              width: "100%",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <button
                              type="button"
                              className={`btn-ged-lista ${Number(registro.qtdArquivos) > 0 ? "btn-ged-ok" : ""}`}
                              onClick={(evento) => {
                                evento.preventDefault();
                                evento.stopPropagation();

                                abrirGed(registro);
                              }}
                              style={{
                                flex: "0 0 116px",
                                width: "116px",
                                minWidth: "116px",
                              }}
                            >
                              GED
                              {Number(registro.qtdArquivos) > 0 && <span className="ged-badge">{registro.qtdArquivos}</span>}
                            </button>

                            <button
                              type="button"
                              className="btn-editar"
                              onClick={() => visualizarRegistro(registro)}
                              style={{
                                flex: "0 0 130px",
                                width: "130px",
                                minWidth: "130px",
                              }}
                            >
                              <FaEye />
                              Visualizar
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <TablePagination
                  component="div"
                  count={registrosFiltrados.length}
                  page={pagina}
                  onPageChange={(_, novaPagina) => setPagina(novaPagina)}
                  rowsPerPage={linhasPorPagina}
                  onRowsPerPageChange={(evento) => {
                    setLinhasPorPagina(Number(evento.target.value));
                    setPagina(0);
                  }}
                  rowsPerPageOptions={[5, 10, 25]}
                  labelRowsPerPage="Registros por página:"
                />
              </>
            )}
          </div>
        )}
        {gedAberto && (
          <div className="modal-ged-overlay">
            <div className="modal-ged">
              <h3>Arquivos GED - DITR ANUAL #{registroGed?.codigoAnual || registroGed?.codigoDitr || registroGed?.id || ""}</h3>

              {carregandoGed ? (
                <p>Carregando arquivos...</p>
              ) : arquivosGed.length === 0 ? (
                <p>Nenhum arquivo cadastrado para este DITR Anual.</p>
              ) : (
                <ul
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  {arquivosGed.map((arquivo) => (
                    <li
                      key={arquivo.id || arquivo.nome}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <span
                        style={{
                          flex: 1,
                        }}
                      >
                        {arquivo.nome}
                      </span>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <button type="button" className="btn-visualizar-ged" onClick={() => abrirArquivo(arquivo)}>
                          <FaEye />
                          Visualizar
                        </button>

                        <button type="button" className="btn-baixar-ged" onClick={() => baixarArquivo(arquivo)}>
                          Baixar
                        </button>

                        <button type="button" className="btn-excluir-ged" onClick={() => excluirArquivo(arquivo)} disabled={arquivoExcluindo === arquivo.id}>
                          <FaTrash />

                          {arquivoExcluindo === arquivo.id ? "Excluindo..." : "Excluir"}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <button type="button" className="btn-fechar-ged" onClick={fecharGed} disabled={Boolean(arquivoExcluindo)}>
                Fechar
              </button>
            </div>
          </div>
        )}

        <div className="bloco-formulario"></div>
        <div className="bloco-formulario">
          <form
            className="form-grid"
            onSubmit={(evento) => {
              evento.preventDefault();
            }}
          >
            <Box>
              <h2
                style={{
                  margin: 0,
                  color: "var(--ig-t-ffffff)",
                  fontSize: "20px",
                  fontWeight: 300,
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                }}
              >
                CADASTRAR DITR ANUAL
              </h2>
            </Box>

            {/*DADOS DO IMÓVEL RURAL */}
            <Box
              onClick={() => alternarSecao("dadosImovel")}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                p: 2,
                borderRadius: 2,
                backgroundColor: "var(--ig-f-1b2433)",
                border: "1px solid var(--ig-b-314158)",
                mb: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    color: "var(--ig-t-ffffff)",
                    textTransform: "uppercase",
                  }}
                >
                  DADOS DO IMÓVEL RURAL
                </h3>

                {indicadorSecao(progressoSecoes.dadosImovel)}
              </Box>

              {secoes.dadosImovel ? <FaChevronUp /> : <FaChevronDown />}
            </Box>

            {secoes.dadosImovel && (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "180px minmax(0, 1fr)",
                    },
                    gap: 2,
                  }}
                >
                  <TextField
                    label="Ano*"
                    value={form.ano}
                    onChange={alterarAno}
                    disabled={modoVisualizacao || editando || Boolean(anoSelecionado)}
                    inputProps={{
                      maxLength: 4,
                      inputMode: "numeric",
                    }}
                    sx={muiCampoSx}
                  />

                  <TextField
                    fullWidth
                    label="Buscar Imóvel Rural*"
                    value={form.codigoDitr ? `${form.codigoDitr} - ${form.apelido || form.nomeImovelRural}` : ""}
                    onClick={() => {
                      if (modoVisualizacao) {
                        return;
                      }

                      if (!form.ano) {
                        exibirMensagem("Selecione primeiro o ano do DITR.", "warning");
                        return;
                      }

                      carregarDitrs(form.ano);
                      setBuscaDitr("");
                      setPaginaDitr(0);
                      setModalDitrAberto(true);
                    }}
                    disabled={modoVisualizacao}
                    sx={muiCampoSx}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Buscar imóvel rural">
                            <IconButton
                              type="button"
                              onClick={(evento) => {
                                evento.stopPropagation();

                                if (modoVisualizacao) {
                                  return;
                                }

                                if (!form.ano) {
                                  exibirMensagem("Selecione primeiro o ano do DITR.", "warning");
                                  return;
                                }

                                carregarDitrs(form.ano);
                                setBuscaDitr("");
                                setPaginaDitr(0);
                                setModalDitrAberto(true);
                              }}
                              disabled={modoVisualizacao}
                            >
                              <FaSearch />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "repeat(3, minmax(0, 1fr))",
                    },
                    gap: 2,
                  }}
                >
                  <TextField
                    select
                    fullWidth
                    label="Imóvel Pertence a um Condomínio?"
                    value={form.pertenceCondominio}
                    onChange={alterarCampo("pertenceCondominio")}
                    disabled={modoVisualizacao}
                    sx={muiCampoSx}
                  >
                    {OPCOES_SIM_NAO.map((opcao) => (
                      <MenuItem key={`condominio-${opcao}`} value={opcao}>
                        {opcao}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    fullWidth
                    label="Imóvel Imune ou Isento?"
                    value={form.imovelImuneIsento}
                    onChange={alterarCampo("imovelImuneIsento")}
                    disabled={modoVisualizacao}
                    sx={muiCampoSx}
                  >
                    {OPCOES_SIM_NAO.map((opcao) => (
                      <MenuItem key={`imune-${opcao}`} value={opcao}>
                        {opcao}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    fullWidth
                    label="Declaração Retificadora?"
                    value={form.declaracaoRetificadora}
                    onChange={alterarCampo("declaracaoRetificadora")}
                    disabled={modoVisualizacao}
                    sx={muiCampoSx}
                  >
                    {OPCOES_SIM_NAO.map((opcao) => (
                      <MenuItem key={`retificadora-${opcao}`} value={opcao}>
                        {opcao}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </>
            )}

            {/* =====================================================
    IDENTIFICAÇÃO DO CONTRIBUINTE
===================================================== */}

            <Box
              onClick={() => alternarSecao("identificacaoContribuinte")}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                p: 2,
                borderRadius: 2,
                backgroundColor: "var(--ig-f-1b2433)",
                border: "1px solid var(--ig-b-314158)",
                mt: 1,
                mb: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    color: "var(--ig-t-ffffff)",
                    textTransform: "uppercase",
                  }}
                >
                  IDENTIFICAÇÃO DO CONTRIBUINTE
                </h3>

                {indicadorSecao(progressoSecoes.identificacaoContribuinte)}
              </Box>

              {secoes.identificacaoContribuinte ? <FaChevronUp /> : <FaChevronDown />}
            </Box>

            {secoes.identificacaoContribuinte && (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "1fr 1fr",
                    },
                    gap: 2,
                  }}
                >
                  <TextField
                    fullWidth
                    label={consultandoDocumento ? "Documento - Consultando Receita..." : "Documento"}
                    value={form.documentoContribuinte}
                    onChange={alterarDocumento}
                    disabled={modoVisualizacao || consultandoDocumento}
                    placeholder="CPF ou CNPJ"
                    inputProps={{
                      maxLength: 18,
                      inputMode: "text",
                    }}
                    sx={muiCampoSx}
                  />

                  <TextField fullWidth label="Tipo de Contribuinte" value={form.tipoContribuinte} disabled sx={muiCampoSx} />
                </Box>

                <>
                  <TextField
                    fullWidth
                    label="Nome"
                    value={form.nomeContribuinte}
                    onChange={alterarCampo("nomeContribuinte")}
                    disabled={modoVisualizacao || consultandoDocumento || nomeBloqueadoReceita}
                    sx={muiCampoSx}
                  />

                  {consultandoDocumento && (
                    <Box
                      sx={{
                        mt: 0.8,
                        ml: 0.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: "#2ea8ff",
                          animation: "pulseReceita 1.2s infinite",
                        }}
                      />

                      <Typography
                        variant="caption"
                        sx={{
                          color: "var(--ig-t-9ecbff)",
                          fontStyle: "italic",
                          fontSize: "0.84rem",
                        }}
                      >
                        Consultando a Razão Social nos registros da Receita Federal...
                      </Typography>
                    </Box>
                  )}
                </>
              </>
            )}

            {/* =====================================================
    DISTRIBUIÇÃO DA ÁREA DO IMÓVEL RURAL
===================================================== */}

            <Box
              onClick={() => alternarSecao("distribuicaoAreaImovel")}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                p: 2,
                borderRadius: 2,
                backgroundColor: "var(--ig-f-1b2433)",
                border: "1px solid var(--ig-b-314158)",
                mt: 1,
                mb: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    color: "var(--ig-t-ffffff)",
                    textTransform: "uppercase",
                  }}
                >
                  DISTRIBUIÇÃO DA ÁREA DO IMÓVEL RURAL
                </h3>

                {indicadorSecao(progressoSecoes.distribuicaoAreaImovel)}
              </Box>

              {secoes.distribuicaoAreaImovel ? <FaChevronUp /> : <FaChevronDown />}
            </Box>

            {secoes.distribuicaoAreaImovel && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                <TextField
                  fullWidth
                  label="Área Total do Imóvel (ha)"
                  value={form.areaTotalImovel}
                  onChange={alterarCampoArea("areaTotalImovel")}
                  onBlur={finalizarCampoArea("areaTotalImovel")}
                  disabled={modoVisualizacao}
                  inputProps={{ inputMode: "decimal" }}
                  sx={muiCampoSx}
                />

                <TextField
                  fullWidth
                  label="Preservação Permanente (ha)"
                  value={form.areaPreservacaoPermanente}
                  onChange={alterarCampoArea("areaPreservacaoPermanente")}
                  onBlur={finalizarCampoArea("areaPreservacaoPermanente")}
                  disabled={modoVisualizacao}
                  inputProps={{ inputMode: "decimal" }}
                  sx={muiCampoSx}
                />

                <TextField
                  fullWidth
                  label="Reserva Particular do Patrimônio Natural (ha)"
                  value={form.areaReservaParticularPatrimonioNatural}
                  onChange={alterarCampoArea("areaReservaParticularPatrimonioNatural")}
                  onBlur={finalizarCampoArea("areaReservaParticularPatrimonioNatural")}
                  disabled={modoVisualizacao}
                  inputProps={{ inputMode: "decimal" }}
                  sx={muiCampoSx}
                />

                <TextField
                  fullWidth
                  label="Interesse Ecológico (ha)"
                  value={form.areaInteresseEcologico}
                  onChange={alterarCampoArea("areaInteresseEcologico")}
                  onBlur={finalizarCampoArea("areaInteresseEcologico")}
                  disabled={modoVisualizacao}
                  inputProps={{ inputMode: "decimal" }}
                  sx={muiCampoSx}
                />

                <TextField
                  fullWidth
                  label="Servidão Ambiental (ha)"
                  value={form.areaServidaoAmbiental}
                  onChange={alterarCampoArea("areaServidaoAmbiental")}
                  onBlur={finalizarCampoArea("areaServidaoAmbiental")}
                  disabled={modoVisualizacao}
                  inputProps={{ inputMode: "decimal" }}
                  sx={muiCampoSx}
                />

                <TextField
                  fullWidth
                  label="Coberta por Florestas Nativas (ha)"
                  value={form.areaCobertaFlorestasNativas}
                  onChange={alterarCampoArea("areaCobertaFlorestasNativas")}
                  onBlur={finalizarCampoArea("areaCobertaFlorestasNativas")}
                  disabled={modoVisualizacao}
                  inputProps={{ inputMode: "decimal" }}
                  sx={muiCampoSx}
                />

                <TextField
                  fullWidth
                  label="Área Alagada de Reservatório de Usinas Hidrelétricas Autorizada pelo Poder Público (ha)"
                  value={form.areaAlagadaReservatorioUsina}
                  onChange={alterarCampoArea("areaAlagadaReservatorioUsina")}
                  onBlur={finalizarCampoArea("areaAlagadaReservatorioUsina")}
                  disabled={modoVisualizacao}
                  inputProps={{ inputMode: "decimal" }}
                  sx={muiCampoSx}
                />

                <TextField
                  fullWidth
                  label="Área Tributável (ha)"
                  value={form.areaTributavel}
                  onChange={alterarCampoArea("areaTributavel")}
                  onBlur={finalizarCampoArea("areaTributavel")}
                  disabled={modoVisualizacao}
                  inputProps={{ inputMode: "decimal" }}
                  sx={muiCampoSx}
                />

                <TextField
                  fullWidth
                  label="Área Ocupada com Benfeitorias Úteis e Necessárias Destinadas à Atividade Rural (ha)"
                  value={form.areaBenfeitoriasAtividadeRural}
                  onChange={alterarCampoArea("areaBenfeitoriasAtividadeRural")}
                  onBlur={finalizarCampoArea("areaBenfeitoriasAtividadeRural")}
                  disabled={modoVisualizacao}
                  inputProps={{ inputMode: "decimal" }}
                  sx={muiCampoSx}
                />

                <TextField
                  fullWidth
                  label="Área Aproveitável (ha)"
                  value={form.areaAproveitavel}
                  onChange={alterarCampoArea("areaAproveitavel")}
                  onBlur={finalizarCampoArea("areaAproveitavel")}
                  disabled={modoVisualizacao}
                  inputProps={{ inputMode: "decimal" }}
                  sx={muiCampoSx}
                />
              </Box>
            )}

            {/* =====================================================
    DISTRIBUIÇÃO DA ÁREA UTILIZADA NA ATIVIDADE RURAL
===================================================== */}

            <Box
              onClick={() => alternarSecao("distribuicaoAreaUtilizada")}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                p: 2,
                borderRadius: 2,
                backgroundColor: "var(--ig-f-1b2433)",
                border: "1px solid var(--ig-b-314158)",
                mt: 1,
                mb: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    color: "var(--ig-t-ffffff)",
                    textTransform: "uppercase",
                  }}
                >
                  DISTRIBUIÇÃO DA ÁREA UTILIZADA NA ATIVIDADE RURAL
                </h3>

                {indicadorSecao(progressoSecoes.distribuicaoAreaUtilizada)}
              </Box>

              {secoes.distribuicaoAreaUtilizada ? <FaChevronUp /> : <FaChevronDown />}
            </Box>

            {secoes.distribuicaoAreaUtilizada && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                <TextField
                  fullWidth
                  label="Produtos Vegetais (ha)"
                  value={form.areaProdutosVegetais}
                  onChange={alterarCampoArea("areaProdutosVegetais")}
                  onBlur={finalizarCampoArea("areaProdutosVegetais")}
                  disabled={modoVisualizacao}
                  inputProps={{ inputMode: "decimal" }}
                  sx={muiCampoSx}
                />

                <TextField
                  fullWidth
                  label="Área em Descanso (ha)"
                  value={form.areaDescanso}
                  onChange={alterarCampoArea("areaDescanso")}
                  onBlur={finalizarCampoArea("areaDescanso")}
                  disabled={modoVisualizacao}
                  inputProps={{ inputMode: "decimal" }}
                  sx={muiCampoSx}
                />

                <TextField
                  fullWidth
                  label="Reflorestamento (Essências Exóticas ou Nativas) (ha)"
                  value={form.areaReflorestamento}
                  onChange={alterarCampoArea("areaReflorestamento")}
                  onBlur={finalizarCampoArea("areaReflorestamento")}
                  disabled={modoVisualizacao}
                  inputProps={{ inputMode: "decimal" }}
                  sx={muiCampoSx}
                />

                <TextField
                  fullWidth
                  label="Pastagem (ha)"
                  value={form.areaPastagem}
                  onChange={alterarCampoArea("areaPastagem")}
                  onBlur={finalizarCampoArea("areaPastagem")}
                  disabled={modoVisualizacao}
                  inputProps={{ inputMode: "decimal" }}
                  sx={muiCampoSx}
                />

                <TextField
                  fullWidth
                  label="Exploração Extrativa (ha)"
                  value={form.areaExploracaoExtrativa}
                  onChange={alterarCampoArea("areaExploracaoExtrativa")}
                  onBlur={finalizarCampoArea("areaExploracaoExtrativa")}
                  disabled={modoVisualizacao}
                  inputProps={{ inputMode: "decimal" }}
                  sx={muiCampoSx}
                />

                <TextField
                  fullWidth
                  label="Atividade Granjeira ou Aquícola (ha)"
                  value={form.areaAtividadeGranjeiraAquicola}
                  onChange={alterarCampoArea("areaAtividadeGranjeiraAquicola")}
                  onBlur={finalizarCampoArea("areaAtividadeGranjeiraAquicola")}
                  disabled={modoVisualizacao}
                  inputProps={{ inputMode: "decimal" }}
                  sx={muiCampoSx}
                />

                <TextField
                  fullWidth
                  label="Frustração de Safra ou Destruição de Pastagem por Calamidade Pública (ha)"
                  value={form.areaFrustracaoSafraCalamidade}
                  onChange={alterarCampoArea("areaFrustracaoSafraCalamidade")}
                  onBlur={finalizarCampoArea("areaFrustracaoSafraCalamidade")}
                  disabled={modoVisualizacao}
                  inputProps={{ inputMode: "decimal" }}
                  sx={muiCampoSx}
                />

                <TextField
                  fullWidth
                  label="Área Utilizada na Atividade Rural (ha)"
                  value={form.areaUtilizadaAtividadeRural}
                  onChange={alterarCampoArea("areaUtilizadaAtividadeRural")}
                  onBlur={finalizarCampoArea("areaUtilizadaAtividadeRural")}
                  disabled={modoVisualizacao}
                  inputProps={{ inputMode: "decimal" }}
                  sx={muiCampoSx}
                />

                <TextField
                  fullWidth
                  label="Grau de Utilização (%)"
                  value={form.grauUtilizacao}
                  onChange={(evento) => {
                    let valor = evento.target.value.replace(/[^\d,]/g, "");

                    const partes = valor.split(",");

                    if (partes.length > 2) {
                      valor = `${partes[0]},${partes.slice(1).join("")}`;
                    }

                    if (valor.includes(",")) {
                      const [inteiro, decimal = ""] = valor.split(",");

                      valor = `${inteiro.slice(0, 3)},${decimal.slice(0, 4)}`;
                    } else {
                      valor = valor.slice(0, 3);
                    }

                    setForm((anterior) => ({
                      ...anterior,
                      grauUtilizacao: valor,
                    }));
                  }}
                  disabled={modoVisualizacao}
                  inputProps={{
                    inputMode: "decimal",
                  }}
                  sx={muiCampoSx}
                />
              </Box>
            )}

            {/* =====================================================
    INFORMAÇÕES AMBIENTAIS
===================================================== */}

            <Box
              onClick={() => alternarSecao("informacoesAmbientais")}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                p: 2,
                borderRadius: 2,
                backgroundColor: "var(--ig-f-1b2433)",
                border: "1px solid var(--ig-b-314158)",
                mt: 1,
                mb: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    color: "var(--ig-t-ffffff)",
                    textTransform: "uppercase",
                  }}
                >
                  INFORMAÇÕES AMBIENTAIS
                </h3>

                {indicadorSecao(progressoSecoes.informacoesAmbientais)}
              </Box>

              {secoes.informacoesAmbientais ? <FaChevronUp /> : <FaChevronDown />}
            </Box>

            {secoes.informacoesAmbientais && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "1fr 1fr",
                  },
                  gap: 2,
                }}
              >
                <TextField
                  fullWidth
                  label="Número do Recibo do ADA ANO / IBAMA"
                  value={form.numeroReciboAdaIbama}
                  onChange={alterarCampo("numeroReciboAdaIbama")}
                  disabled={modoVisualizacao}
                  sx={muiCampoSx}
                />

                <TextField fullWidth label="Número do CAR" value={form.numeroCar} disabled sx={muiCampoSx} />
              </Box>
            )}

            {/* =====================================================
    DISTRIBUIÇÃO DA ÁREA NÃO UTILIZADA
===================================================== */}

            <Box
              onClick={() => alternarSecao("areaNaoUtilizada")}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                p: 2,
                borderRadius: 2,
                backgroundColor: "var(--ig-f-1b2433)",
                border: "1px solid var(--ig-b-314158)",
                mt: 1,
                mb: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    color: "var(--ig-t-ffffff)",
                    textTransform: "uppercase",
                  }}
                >
                  DISTRIBUIÇÃO DA ÁREA NÃO UTILIZADA NA ATIVIDADE RURAL
                </h3>

                {indicadorSecao(progressoSecoes.areaNaoUtilizada)}
              </Box>

              {secoes.areaNaoUtilizada ? <FaChevronUp /> : <FaChevronDown />}
            </Box>

            {secoes.areaNaoUtilizada && (
              <TextField
                fullWidth
                label="Área Total Não Utilizada na Atividade Rural (ha)"
                value={form.areaTotalNaoUtilizada}
                onChange={alterarCampoArea("areaTotalNaoUtilizada")}
                onBlur={finalizarCampoArea("areaTotalNaoUtilizada")}
                disabled={modoVisualizacao}
                inputProps={{
                  inputMode: "decimal",
                }}
                sx={muiCampoSx}
              />
            )}

            {/* =====================================================
    CÁLCULO DO VALOR DA TERRA NUA
===================================================== */}

            <Box
              onClick={() => alternarSecao("valorTerraNua")}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                p: 2,
                borderRadius: 2,
                backgroundColor: "var(--ig-f-1b2433)",
                border: "1px solid var(--ig-b-314158)",
                mt: 1,
                mb: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    color: "var(--ig-t-ffffff)",
                    textTransform: "uppercase",
                  }}
                >
                  CÁLCULO DO VALOR DA TERRA NUA
                </h3>

                {indicadorSecao(progressoSecoes.valorTerraNua)}
              </Box>

              {secoes.valorTerraNua ? <FaChevronUp /> : <FaChevronDown />}
            </Box>

            {secoes.valorTerraNua && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                <TextField
                  fullWidth
                  label="Valor Total do Imóvel"
                  value={form.valorTotalImovel}
                  onChange={alterarCampoMoeda("valorTotalImovel")}
                  disabled={modoVisualizacao}
                  inputProps={{ inputMode: "numeric" }}
                  sx={muiCampoSx}
                />

                <TextField
                  fullWidth
                  label="Valor das Construções, Instalações e Benfeitorias"
                  value={form.valorConstrucoesBenfeitorias}
                  onChange={alterarCampoMoeda("valorConstrucoesBenfeitorias")}
                  disabled={modoVisualizacao}
                  inputProps={{ inputMode: "numeric" }}
                  sx={muiCampoSx}
                />

                <TextField
                  fullWidth
                  label="Valor das Culturas, Pastagens Cultivadas e Melhoradas e Florestas Plantadas"
                  value={form.valorCulturasPastagensFlorestas}
                  onChange={alterarCampoMoeda("valorCulturasPastagensFlorestas")}
                  disabled={modoVisualizacao}
                  inputProps={{ inputMode: "numeric" }}
                  sx={muiCampoSx}
                />

                <TextField
                  fullWidth
                  label="Valor Terra Nua"
                  value={form.valorTerraNua}
                  onChange={alterarCampoMoeda("valorTerraNua")}
                  disabled={modoVisualizacao}
                  inputProps={{ inputMode: "numeric" }}
                  sx={muiCampoSx}
                />
              </Box>
            )}

            {/* =====================================================
    CÁLCULO DO IMPOSTO
===================================================== */}

            <Box
              onClick={() => alternarSecao("calculoImposto")}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                p: 2,
                borderRadius: 2,
                backgroundColor: "var(--ig-f-1b2433)",
                border: "1px solid var(--ig-b-314158)",
                mt: 1,
                mb: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    color: "var(--ig-t-ffffff)",
                    textTransform: "uppercase",
                  }}
                >
                  CÁLCULO DO IMPOSTO
                </h3>

                {indicadorSecao(progressoSecoes.calculoImposto)}
              </Box>

              {secoes.calculoImposto ? <FaChevronUp /> : <FaChevronDown />}
            </Box>

            {secoes.calculoImposto && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                <TextField
                  fullWidth
                  label="Valor da Terra Nua Tributável"
                  value={form.valorTerraNuaTributavel}
                  onChange={alterarCampoMoeda("valorTerraNuaTributavel")}
                  disabled={modoVisualizacao}
                  inputProps={{
                    inputMode: "numeric",
                  }}
                  sx={muiCampoSx}
                />

                <TextField
                  fullWidth
                  label="Alíquota (%)"
                  value={form.aliquota}
                  onChange={(evento) => {
                    const valor = evento.target.value.replace(/[^\d,]/g, "").slice(0, 8);

                    setForm((anterior) => ({
                      ...anterior,
                      aliquota: valor,
                    }));
                  }}
                  disabled={modoVisualizacao}
                  inputProps={{
                    inputMode: "decimal",
                  }}
                  sx={muiCampoSx}
                />

                <TextField fullWidth label="Imposto Calculado" value={form.impostoCalculado} disabled sx={muiCampoSx} />

                <TextField
                  fullWidth
                  label="Imposto Devido"
                  value={form.impostoDevido}
                  onChange={alterarCampoMoeda("impostoDevido")}
                  disabled={modoVisualizacao}
                  inputProps={{
                    inputMode: "numeric",
                  }}
                  sx={muiCampoSx}
                />

                <TextField
                  fullWidth
                  label="Quantidade de Quotas"
                  value={form.quantidadeQuotas}
                  onChange={(evento) => {
                    const valor = evento.target.value.replace(/\D/g, "").slice(0, 3);

                    setForm((anterior) => ({
                      ...anterior,
                      quantidadeQuotas: valor,
                    }));
                  }}
                  disabled={modoVisualizacao}
                  inputProps={{
                    inputMode: "numeric",
                  }}
                  sx={muiCampoSx}
                />

                <TextField fullWidth label="Valor da Quota ou da Quota Única" value={form.valorQuota} disabled sx={muiCampoSx} />
              </Box>
            )}

            {/* =====================================================
    INFORMAÇÕES SOBRE ATIVIDADE PECUÁRIA
===================================================== */}

            <Box
              onClick={() => alternarSecao("atividadePecuaria")}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                p: 2,
                borderRadius: 2,
                backgroundColor: "var(--ig-f-1b2433)",
                border: "1px solid var(--ig-b-314158)",
                mt: 1,
                mb: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    color: "var(--ig-t-ffffff)",
                    textTransform: "uppercase",
                  }}
                >
                  INFORMAÇÕES SOBRE ATIVIDADE PECUÁRIA
                </h3>

                {indicadorSecao(progressoSecoes.atividadePecuaria)}
              </Box>

              {secoes.atividadePecuaria ? <FaChevronUp /> : <FaChevronDown />}
            </Box>

            {secoes.atividadePecuaria && (
              <TextField
                fullWidth
                label="Quantidade Total de Cabeça do Rebanho Ajustada"
                value={form.quantidadeCabecasRebanho}
                onChange={(evento) => {
                  const digitos = evento.target.value.replace(/\D/g, "").slice(0, 12);

                  const valorFormatado = digitos.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

                  setForm((anterior) => ({
                    ...anterior,
                    quantidadeCabecasRebanho: valorFormatado,
                  }));
                }}
                disabled={modoVisualizacao}
                inputProps={{
                  inputMode: "numeric",
                }}
                sx={muiCampoSx}
              />
            )}
            {/* =====================================================
    INFORMAÇÕES SOBRE ATIVIDADES EXTRATIVA VEGETAL
    E FLORESTAL
===================================================== */}

            <Box
              onClick={() => alternarSecao("atividadeExtrativa")}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                p: 2,
                borderRadius: 2,
                backgroundColor: "var(--ig-f-1b2433)",
                border: "1px solid var(--ig-b-314158)",
                mt: 1,
                mb: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    color: "var(--ig-t-ffffff)",
                    textTransform: "uppercase",
                  }}
                >
                  INFORMAÇÕES SOBRE ATIVIDADES EXTRATIVA VEGETAL E FLORESTAL
                </h3>

                {indicadorSecao(progressoSecoes.atividadeExtrativa)}
              </Box>

              {secoes.atividadeExtrativa ? <FaChevronUp /> : <FaChevronDown />}
            </Box>

            {secoes.atividadeExtrativa && (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "2fr 1fr",
                    },
                    gap: 2,
                  }}
                >
                  <TextField
                    fullWidth
                    label="Área com Exploração Extrativa Aceita (ha)"
                    value={form.areaExploracaoExtrativaAceita}
                    onChange={alterarCampoArea("areaExploracaoExtrativaAceita")}
                    onBlur={finalizarCampoArea("areaExploracaoExtrativaAceita")}
                    disabled={modoVisualizacao}
                    inputProps={{
                      inputMode: "decimal",
                    }}
                    sx={muiCampoSx}
                  />

                  <TextField
                    select
                    fullWidth
                    label="Status"
                    value={form.statusExtrativa}
                    onChange={(evento) => {
                      const status = evento.target.value;

                      setForm((anterior) => ({
                        ...anterior,
                        statusExtrativa: status,

                        observacaoNaoConforme: status === "NÃO CONFORME" ? anterior.observacaoNaoConforme : "",

                        dataAvisoNaoConforme: status === "NÃO CONFORME" ? anterior.dataAvisoNaoConforme : "",
                      }));
                    }}
                    disabled={modoVisualizacao}
                    sx={muiCampoSx}
                  >
                    <MenuItem value="CONFORME">CONFORME</MenuItem>

                    <MenuItem value="NÃO CONFORME">NÃO CONFORME</MenuItem>
                  </TextField>
                </Box>

                {form.statusExtrativa === "NÃO CONFORME" && (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "2fr 1fr",
                      },
                      gap: 2,
                    }}
                  >
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Observação"
                      value={form.observacaoNaoConforme}
                      onChange={alterarCampo("observacaoNaoConforme")}
                      disabled={modoVisualizacao}
                      sx={muiCampoSx}
                    />

                    <TextField
                      fullWidth
                      label="Data de Aviso"
                      type="date"
                      value={form.dataAvisoNaoConforme}
                      onChange={(evento) => {
                        setForm((anterior) => ({
                          ...anterior,
                          dataAvisoNaoConforme: evento.target.value,
                        }));
                      }}
                      disabled={modoVisualizacao}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                        htmlInput: {
                          max: "9999-12-31",
                        },
                      }}
                      sx={{
                        ...muiCampoSx,

                        "& input[type='date']": {
                          colorScheme: "var(--ig-e-1txfa)",
                        },

                        "& input[type='date']::-webkit-calendar-picker-indicator": {
                          filter: "var(--ig-x-11o99ka)",
                          cursor: "pointer",
                        },
                      }}
                    />
                  </Box>
                )}
              </>
            )}

            {/* =====================================================
    OBSERVAÇÃO GERAL
===================================================== */}

            <Box
              sx={{
                mt: 2,
                mb: 1,
              }}
            >
              <h3
                style={{
                  margin: 0,
                  color: "var(--ig-t-ffffff)",
                  textTransform: "uppercase",
                }}
              >
                OBSERVAÇÃO GERAL
              </h3>
            </Box>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Observação"
              value={form.observacao}
              onChange={alterarCampo("observacao")}
              disabled={modoVisualizacao}
              sx={muiCampoSx}
            />

            {!modoVisualizacao && arquivosPendentes.length > 0 && (
              <Box
                sx={{
                  border: "1px solid rgba(46, 168, 255, 0.45)",
                  borderRadius: "4px",
                  p: 1.5,
                  backgroundColor: "rgba(46, 168, 255, 0.06)",
                }}
              >
                <Box
                  sx={{
                    color: "var(--ig-t-90caf9)",
                    fontWeight: 700,
                    mb: 1,
                  }}
                >
                  Arquivos selecionados para envio
                </Box>

                {arquivosPendentes.map((arquivo, indice) => (
                  <Box
                    key={`${arquivo.name}-${indice}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      py: 0.5,
                      borderBottom: indice < arquivosPendentes.length - 1 ? "1px solid var(--ig-b-255-255-255-a012)" : "none",
                    }}
                  >
                    <span>{arquivo.name}</span>

                    <button type="button" className="btn-excluir-ged" onClick={() => removerArquivo(indice)} disabled={salvando}>
                      <FaTrash />
                      Remover
                    </button>
                  </Box>
                ))}
              </Box>
            )}

            <input id="uploadGEDDITRAnual" type="file" multiple onChange={selecionarArquivos} style={{ display: "none" }} />

            <div className="iptu-botoes">
              {modoVisualizacao ? (
                <>
                  <button type="button" className="iptu-btn-acao btn-cadastrar" onClick={iniciarEdicao}>
                    <FaEdit className="iptu-btn-icone" />
                    Editar
                  </button>

                  <button type="button" className="iptu-btn-acao btn-excluir" onClick={excluirRegistro} disabled={excluindo}>
                    <FaTrash className="iptu-btn-icone" />

                    {excluindo ? "Excluindo..." : "Excluir"}
                  </button>

                  <button type="button" className="iptu-btn-acao btn-acao" onClick={() => limparFormulario(anoSelecionado || form.ano)}>
                    <FaTimes className="iptu-btn-icone" />
                    Cancelar
                  </button>

                  <button
                    type="button"
                    className={`iptu-btn-acao btn-ged ${
                      Number(registros.find((item) => Number(item.id) === Number(form.id))?.qtdArquivos) > 0 ? "btn-ged-ok" : ""
                    }`}
                    onClick={(evento) => {
                      evento.preventDefault();
                      evento.stopPropagation();

                      const registroAtual = registros.find((item) => Number(item.id) === Number(form.id)) || form;

                      abrirGed(registroAtual);
                    }}
                  >
                    <FaPaperclip className="iptu-btn-icone" />
                    GED
                  </button>
                </>
              ) : editando ? (
                <>
                  <button
                    type="button"
                    className="iptu-btn-acao btn-cadastrar"
                    onClick={() => {
                      if (!salvando) {
                        salvar();
                      }
                    }}
                    disabled={salvando}
                  >
                    <FaEdit className="iptu-btn-icone" />

                    {salvando ? "Salvando..." : "Atualizar"}
                  </button>

                  <button type="button" className="iptu-btn-acao btn-acao" onClick={() => limparFormulario(anoSelecionado || form.ano)} disabled={salvando}>
                    <FaTimes className="iptu-btn-icone" />
                    Cancelar
                  </button>

                  <button
                    type="button"
                    className={`iptu-btn-acao btn-ged ${arquivosPendentes.length > 0 ? "btn-ged-ok" : ""}`}
                    onClick={(evento) => {
                      evento.preventDefault();
                      evento.stopPropagation();

                      document.getElementById("uploadGEDDITRAnual")?.click();
                    }}
                    disabled={salvando}
                  >
                    <FaPaperclip className="iptu-btn-icone" />
                    GED
                    {arquivosPendentes.length > 0 && <span className="ged-badge">{arquivosPendentes.length}</span>}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="iptu-btn-acao btn-cadastrar"
                    onClick={() => {
                      if (!salvando) {
                        salvar();
                      }
                    }}
                    disabled={salvando}
                  >
                    <FaSave className="iptu-btn-icone" />

                    {salvando ? "Salvando..." : "Cadastrar"}
                  </button>

                  <button type="button" className="iptu-btn-acao btn-acao" onClick={() => limparFormulario(anoSelecionado || form.ano)} disabled={salvando}>
                    <FaTimes className="iptu-btn-icone" />
                    Cancelar
                  </button>

                  <button
                    type="button"
                    className={`iptu-btn-acao btn-ged ${arquivosPendentes.length > 0 ? "btn-ged-ok" : ""}`}
                    onClick={(evento) => {
                      evento.preventDefault();
                      evento.stopPropagation();

                      document.getElementById("uploadGEDDITRAnual")?.click();
                    }}
                    disabled={salvando}
                  >
                    <FaPaperclip className="iptu-btn-icone" />
                    GED
                    {arquivosPendentes.length > 0 && <span className="ged-badge">{arquivosPendentes.length}</span>}
                  </button>
                </>
              )}
            </div>
          </form>
        </div>

        <Dialog
          open={modalDitrAberto}
          onClose={() => {
            setModalDitrAberto(false);
            setBuscaDitr("");
            setPaginaDitr(0);
          }}
          maxWidth="lg"
          fullWidth
          slotProps={{
            paper: {
              sx: {
                backgroundColor: "var(--ig-f-2c2c2c)",
                color: "var(--ig-t-ffffff)",
                backgroundImage: "none",
              },
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontWeight: 700,
              color: "var(--ig-t-ffffff)",
            }}
          >
            Selecionar DITR Base - Ano {form.ano}
            <IconButton
              type="button"
              onClick={() => {
                setModalDitrAberto(false);
                setBuscaDitr("");
                setPaginaDitr(0);
              }}
              aria-label="Fechar janela"
            >
              <CloseIcon
                sx={{
                  color: "var(--ig-t-ffffff)",
                }}
              />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <TextField
              fullWidth
              value={buscaDitr}
              onChange={(evento) => {
                setBuscaDitr(evento.target.value);
                setPaginaDitr(0);
              }}
              placeholder="Código DITR, CCIR, imóvel rural, CAR, matrícula, apelido, município ou UF..."
              sx={{
                ...muiCampoSx,
                mb: 2,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>DITR</TableCell>
                  <TableCell>CCIR</TableCell>
                  <TableCell>Imóvel Rural</TableCell>
                  <TableCell>CAR</TableCell>
                  <TableCell>Apelido</TableCell>
                  <TableCell>Matrícula</TableCell>
                  <TableCell>Município/UF</TableCell>
                  <TableCell>Área Total</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {ditrsPaginados.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      align="center"
                      sx={{
                        color: "var(--ig-t-ffffff)",
                        py: 3,
                      }}
                    >
                      Nenhum DITR disponível para o ano informado.
                    </TableCell>
                  </TableRow>
                ) : (
                  ditrsPaginados.map((ditr) => (
                    <TableRow
                      key={ditr.id ?? ditr.ditrId ?? ditr.codigoDitr}
                      hover
                      onClick={() => selecionarDitr(ditr)}
                      sx={{
                        cursor: "pointer",
                      }}
                    >
                      <TableCell>{ditr.codigoDitr || ""}</TableCell>

                      <TableCell>{ditr.codigoCcir || ""}</TableCell>

                      <TableCell>{ditr.nomeImovelRural || ""}</TableCell>

                      <TableCell>{ditr.numeroCar ?? ditr.codigoCar ?? ditr.registroCar ?? ditr.registroNoCar ?? ""}</TableCell>

                      <TableCell>{ditr.apelido || ""}</TableCell>

                      <TableCell>{ditr.codigoMatricula || ""}</TableCell>

                      <TableCell>{[ditr.municipioSedeImovelRural ?? ditr.municipio, ditr.uf].filter(Boolean).join("/")}</TableCell>

                      <TableCell>{formatarArea(ditr.areaTotalImovel ?? ditr.areaTotalHa)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={ditrsFiltrados.length}
              page={paginaDitr}
              onPageChange={(_, novaPagina) => setPaginaDitr(novaPagina)}
              rowsPerPage={linhasPorPaginaDitr}
              onRowsPerPageChange={(evento) => {
                setLinhasPorPaginaDitr(Number(evento.target.value));

                setPaginaDitr(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              sx={{
                color: "var(--ig-t-ffffff)",

                "& .MuiTablePagination-selectIcon": {
                  color: "var(--ig-t-ffffff)",
                },

                "& .MuiTablePagination-actions button": {
                  color: "var(--ig-t-ffffff)",
                },
              }}
            />
          </DialogContent>
        </Dialog>

        <Snackbar open={mensagem.aberta} autoHideDuration={5000} onClose={fecharMensagem}>
          <Alert severity={mensagem.tipo} onClose={fecharMensagem}>
            {mensagem.texto}
          </Alert>
        </Snackbar>
      </div>
    </ThemeProvider>
  );
}

const muiCampoSx = {
  "& .MuiOutlinedInput-root": {
    color: "var(--ig-t-ffffff)",
    backgroundColor: "transparent",
    "& fieldset": {
      borderColor: "var(--ig-b-555555)",
    },
    "&:hover fieldset": {
      borderColor: "var(--ig-b-777777)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#2ea8ff",
    },
    "&.Mui-disabled .MuiInputBase-input": {
      WebkitTextFillColor: "var(--ig-t-ffffff)",
      opacity: 1,
    },
  },
  "& .MuiInputLabel-root": {
    color: "var(--ig-t-cbd5e1)",
  },
  "& .MuiInputBase-input": {
    color: "var(--ig-t-ffffff)",
  },
};

const receitaPulseStyle = `
@keyframes pulseReceita {
  0% {
    opacity: .35;
    transform: scale(.9);
  }

  50% {
    opacity: 1;
    transform: scale(1.25);
  }

  100% {
    opacity: .35;
    transform: scale(.9);
  }
}
`;
